// Morse code lookup tables
// Dot = short signal, Dash = long signal

export type MorseSymbol = '.' | '-';
export type MorseSequence = MorseSymbol[];

// Character to morse code mapping
const CHAR_TO_MORSE: Record<string, string> = {
  'A': '.-',
  'B': '-...',
  'C': '-.-.',
  'D': '-..',
  'E': '.',
  'F': '..-.',
  'G': '--.',
  'H': '....',
  'I': '..',
  'J': '.---',
  'K': '-.-',
  'L': '.-..',
  'M': '--',
  'N': '-.',
  'O': '---',
  'P': '.--.',
  'Q': '--.-',
  'R': '.-.',
  'S': '...',
  'T': '-',
  'U': '..-',
  'V': '...-',
  'W': '.--',
  'X': '-..-',
  'Y': '-.--',
  'Z': '--..',
  '0': '-----',
  '1': '.----',
  '2': '..---',
  '3': '...--',
  '4': '....-',
  '5': '.....',
  '6': '-....',
  '7': '--...',
  '8': '---..',
  '9': '----.',
  '.': '.-.-.-',
  ',': '--..--',
  '?': '..--..',
  '!': '-.-.--',
  '/': '-..-.',
  '(': '-.--.',
  ')': '-.--.-',
  '&': '.-...',
  ':': '---...',
  ';': '-.-.-.',
  '=': '-...-',
  '+': '.-.-.',
  '-': '-....-',
  '_': '..--.-',
  '"': '.-..-.',
  '$': '...-..-',
  '@': '.--.-.',
  "'": '.----.',
  ' ': ' ', // Space between words
};

// Morse code to character mapping (reverse lookup)
const MORSE_TO_CHAR: Record<string, string> = {};
for (const [char, morse] of Object.entries(CHAR_TO_MORSE)) {
  if (morse !== ' ') {
    MORSE_TO_CHAR[morse] = char;
  }
}

/**
 * Convert a character to its morse code representation
 */
export function charToMorse(char: string): string {
  const upper = char.toUpperCase();
  const morse = CHAR_TO_MORSE[upper];
  if (morse === undefined) {
    throw new Error(`Invalid character for morse conversion: "${char}"`);
  }
  return morse;
}

/**
 * Convert a morse code sequence to its character
 */
export function morseToChar(morse: string): string {
  const char = MORSE_TO_CHAR[morse];
  if (char === undefined) {
    throw new Error(`Invalid morse sequence: "${morse}"`);
  }
  return char;
}

/**
 * Convert a text message to an array of morse sequences
 * Each element is either a MorseSequence for a letter or null for space
 */
export function textToMorseSequences(text: string): (MorseSequence | null)[] {
  const sequences: (MorseSequence | null)[] = [];

  for (const char of text.toUpperCase()) {
    if (char === ' ') {
      sequences.push(null); // Word separator
    } else {
      const morse = CHAR_TO_MORSE[char];
      if (morse) {
        sequences.push(morse.split('') as MorseSequence);
      }
    }
  }

  return sequences;
}

/**
 * Convert a morse sequence to its character
 * Returns '?' if the sequence is invalid
 */
export function sequenceToChar(sequence: MorseSequence): string {
  const morse = sequence.join('');
  return MORSE_TO_CHAR[morse] ?? '?';
}

/**
 * Get the expected character for a message at a given index
 */
export function getCharAtIndex(message: string, index: number): string | null {
  const upper = message.toUpperCase();
  if (index < 0 || index >= upper.length) {
    return null;
  }
  return upper[index];
}

/**
 * Flatten a message into individual morse symbols with metadata
 */
export interface MorseNote {
  symbol: MorseSymbol;
  charIndex: number;      // Index of the character in the original message
  symbolIndex: number;    // Index of this symbol within the character's morse sequence
  isLastInChar: boolean;  // Is this the last symbol for this character?
  expectedChar: string;   // The character this symbol belongs to
}

export function messageToNotes(message: string): MorseNote[] {
  const notes: MorseNote[] = [];
  const upper = message.toUpperCase();

  let charIndex = 0;
  for (const char of upper) {
    if (char === ' ') {
      charIndex++;
      continue;
    }

    const morse = CHAR_TO_MORSE[char];
    if (!morse) {
      charIndex++;
      continue;
    }

    const symbols = morse.split('') as MorseSymbol[];
    symbols.forEach((symbol, symbolIndex) => {
      notes.push({
        symbol,
        charIndex,
        symbolIndex,
        isLastInChar: symbolIndex === symbols.length - 1,
        expectedChar: char,
      });
    });

    charIndex++;
  }

  return notes;
}

/**
 * Decode a sequence of player inputs into a character
 * This is used when the player finishes a character (after the last symbol)
 */
export function decodePlayerInput(inputs: MorseSymbol[]): string {
  if (inputs.length === 0) {
    return '?';
  }
  const morse = inputs.join('');
  return MORSE_TO_CHAR[morse] ?? '?';
}
